import { useState } from 'react';
import { Settings, User, Shield, Bell, Key, Download, Trash2, Save } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { apiClient } from '@/lib/axios';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useAuthStore } from '@/stores/authStore';
import toast from 'react-hot-toast';

interface ProfileForm {
  name: string;
  email: string;
  phone: string;
}

interface PasswordForm {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

function ProfileSettings() {
  const { user } = useAuthStore();
  const { register, handleSubmit, formState: { errors } } = useForm<ProfileForm>({
    defaultValues: { name: user?.name ?? '', email: user?.email ?? '', phone: user?.phone ?? '' },
  });

  const mutation = useMutation({
    mutationFn: (data: ProfileForm) => apiClient.patch('/users/me', data),
    onSuccess: () => toast.success('Profile updated'),
    onError: () => toast.error('Failed to update profile'),
  });

  return (
    <form onSubmit={handleSubmit(d => mutation.mutate(d))} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <Input {...register('name', { required: 'Required' })} label="Full Name" error={errors.name?.message} />
        <Input {...register('email', { required: 'Required' })} label="Email" type="email" error={errors.email?.message} />
        <Input {...register('phone')} label="Phone" type="tel" placeholder="+91 XXXXX XXXXX" />
      </div>
      <div className="flex justify-end">
        <Button type="submit" loading={mutation.isPending} icon={<Save size={14} />}>
          Save Profile
        </Button>
      </div>
    </form>
  );
}

function PasswordSettings() {
  const { register, handleSubmit, watch, reset, formState: { errors } } = useForm<PasswordForm>();
  const newPwd = watch('newPassword');

  const mutation = useMutation({
    mutationFn: (data: PasswordForm) =>
      apiClient.post('/auth/change-password', {
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      }),
    onSuccess: () => {
      toast.success('Password changed successfully');
      reset();
    },
    onError: () => toast.error('Failed to change password. Check current password.'),
  });

  return (
    <form onSubmit={handleSubmit(d => mutation.mutate(d))} className="space-y-4">
      <Input
        {...register('currentPassword', { required: 'Required' })}
        label="Current Password"
        type="password"
        error={errors.currentPassword?.message}
      />
      <div className="grid grid-cols-2 gap-4">
        <Input
          {...register('newPassword', { required: 'Required', minLength: { value: 8, message: 'Min 8 characters' } })}
          label="New Password"
          type="password"
          error={errors.newPassword?.message}
        />
        <Input
          {...register('confirmPassword', {
            required: 'Required',
            validate: v => v === newPwd || 'Passwords do not match',
          })}
          label="Confirm Password"
          type="password"
          error={errors.confirmPassword?.message}
        />
      </div>
      <div className="flex justify-end">
        <Button type="submit" loading={mutation.isPending} icon={<Key size={14} />}>
          Change Password
        </Button>
      </div>
    </form>
  );
}

function TwoFASettings() {
  const { user } = useAuthStore();
  const [enabled, setEnabled] = useState(false);

  const toggleMutation = useMutation({
    mutationFn: (enable: boolean) =>
      apiClient.post('/auth/2fa/toggle', { enable }),
    onSuccess: (_, enable) => {
      setEnabled(enable);
      toast.success(`Two-factor authentication ${enable ? 'enabled' : 'disabled'}`);
    },
    onError: () => toast.error('Failed to update 2FA settings'),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
        <div>
          <p className="font-medium text-slate-900">Two-Factor Authentication (2FA)</p>
          <p className="text-sm text-slate-500 mt-0.5">
            Receive OTP via SMS for additional security when logging in
          </p>
        </div>
        <button
          onClick={() => toggleMutation.mutate(!enabled)}
          disabled={toggleMutation.isPending}
          className={`relative w-11 h-6 rounded-full transition-colors ${enabled ? 'bg-brand-cyan' : 'bg-slate-300'}`}
        >
          <span
            className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${enabled ? 'translate-x-5' : ''}`}
          />
        </button>
      </div>
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
        HIPAA Recommendation: Enable 2FA to protect access to PHI (Protected Health Information).
      </div>
    </div>
  );
}

function GDPRSettings() {
  const exportMutation = useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.get('/gdpr/export', { responseType: 'blob' });
      const url = URL.createObjectURL(data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `my-data-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
    },
    onSuccess: () => toast.success('Data exported'),
    onError: () => toast.error('Export failed'),
  });

  const eraseMutation = useMutation({
    mutationFn: () => apiClient.post('/gdpr/erasure-request'),
    onSuccess: () => toast.success('Erasure request submitted. You will be contacted within 30 days.'),
    onError: () => toast.error('Failed to submit erasure request'),
  });

  return (
    <div className="space-y-4">
      <div className="p-4 bg-slate-50 rounded-lg">
        <h4 className="font-medium text-slate-900 mb-1">Export My Data</h4>
        <p className="text-sm text-slate-500 mb-3">
          Download all personal data associated with your account (GDPR Article 20).
        </p>
        <Button
          variant="outline"
          size="sm"
          icon={<Download size={14} />}
          onClick={() => exportMutation.mutate()}
          loading={exportMutation.isPending}
        >
          Export Data
        </Button>
      </div>

      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <h4 className="font-medium text-red-800 mb-1">Request Data Erasure</h4>
        <p className="text-sm text-red-600 mb-3">
          Request deletion of all your personal data from our systems (GDPR Article 17).
          This action is irreversible.
        </p>
        <Button
          variant="ghost"
          size="sm"
          icon={<Trash2 size={14} />}
          className="text-red-600 hover:bg-red-100"
          onClick={() => {
            if (confirm('Are you sure? This will permanently delete all your data.')) {
              eraseMutation.mutate();
            }
          }}
          loading={eraseMutation.isPending}
        >
          Request Erasure
        </Button>
      </div>
    </div>
  );
}

const TABS = [
  { id: 'profile', label: 'Profile', icon: User },
  { id: 'password', label: 'Password', icon: Key },
  { id: 'security', label: 'Security (2FA)', icon: Shield },
  { id: 'gdpr', label: 'Privacy & GDPR', icon: Shield },
];

export function SettingsPage() {
  const [activeTab, setActiveTab] = useState('profile');
  const { user } = useAuthStore();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Settings size={24} className="text-brand-navy" />
          Settings
        </h1>
        <p className="text-slate-500 text-sm mt-0.5">Manage your account and preferences</p>
      </div>

      <div className="flex gap-6">
        {/* Sidebar Nav */}
        <div className="w-52 flex-shrink-0">
          <div className="bg-white rounded-xl shadow-card border border-slate-100 overflow-hidden">
            {/* User info */}
            <div className="px-4 py-5 border-b border-slate-100">
              <div className="w-12 h-12 bg-brand-navy rounded-xl flex items-center justify-center text-white font-bold text-lg mb-2">
                {user?.name?.charAt(0).toUpperCase() ?? 'U'}
              </div>
              <p className="font-semibold text-slate-900 text-sm truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 capitalize">{user?.role?.replace(/_/g, ' ')}</p>
            </div>
            <nav className="py-2">
              {TABS.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'bg-brand-navy/5 text-brand-navy font-medium'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <tab.icon size={15} />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1">
          <div className="bg-white rounded-xl shadow-card border border-slate-100 p-6">
            {activeTab === 'profile' && (
              <>
                <h2 className="text-base font-semibold text-slate-900 mb-5">Profile Information</h2>
                <ProfileSettings />
              </>
            )}
            {activeTab === 'password' && (
              <>
                <h2 className="text-base font-semibold text-slate-900 mb-5">Change Password</h2>
                <PasswordSettings />
              </>
            )}
            {activeTab === 'security' && (
              <>
                <h2 className="text-base font-semibold text-slate-900 mb-5">Security Settings</h2>
                <TwoFASettings />
              </>
            )}
            {activeTab === 'gdpr' && (
              <>
                <h2 className="text-base font-semibold text-slate-900 mb-5">Privacy & GDPR</h2>
                <GDPRSettings />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
